import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Grid, 
  Typography, 
  Box,
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Slider,
  InputAdornment,
  Paper,
  Divider,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress
} from '@mui/material';
import SeoHead from '../components/common/SeoHead';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ProductCard from '../components/products/ProductCard';

// 导入Firebase
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/firebase';

// 导入图片
import heroImage from '../assets/images/shop/shopBg.png';

// 样式化组件
const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  overflow: 'hidden'
}));

const HeroImage = styled('img')({
  width: '100%',
  display: 'block'
});

const HeroContent = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  textAlign: 'center',
  width: '100%',
  padding: '0 2rem',
  color: 'white'
}));

const FilterSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  boxShadow: 'none',
  borderRadius: theme.shape.borderRadius
}));

const CategoryRadioGroup = styled(RadioGroup)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  '& .MuiFormControlLabel-root': {
    marginBottom: theme.spacing(1)
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1, 3),
  '&:hover': {
    backgroundColor: theme.palette.primary.dark
  }
}));

// 商店页面组件
const Shop = () => {
  // 状态
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [priceRange, setPriceRange] = useState(0);
  const [maxPrice, setMaxPrice] = useState(500); // 默认最大价格
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [sortByPrice, setSortByPrice] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  
  // 添加SEO结构化数据
  const shopSchemaData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "能量水晶手串 | 提升运势、平衡能量 | DestinyCrystal",
    "description": "精选高品质能量水晶手串，包括紫水晶、粉晶、黑曜石等，帮助平衡能量、增强运势。每款水晶都经过专业净化与加持，立即选购适合您的水晶手串！",
    "url": "https://destiny-crystal.com/shop",
    "isPartOf": {
      "@type": "WebSite",
      "name": "DestinyCrystal命理水晶",
      "url": "https://destiny-crystal.com"
    }
  };

  // 获取分类和产品数据
  useEffect(() => {
    const fetchCategoriesAndProducts = async () => {
      try {
        setLoading(true);
        
        // 获取分类数据
        const categoriesCollection = collection(db, 'categories');
        const categoriesSnapshot = await getDocs(categoriesCollection);
        const categoriesList = categoriesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCategories(categoriesList);
        
        // 获取产品数据
        const productsQuery = query(
          collection(db, 'products'),
          where('status', '==', 'active')
        );
        const productsSnapshot = await getDocs(productsQuery);
        const productsList = productsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // 设置产品数据并找出最高价格
        setProducts(productsList);
        
        // 确定最高价格
        if (productsList.length > 0) {
          const highestPrice = Math.max(...productsList.map(p => p.price));
          setMaxPrice(Math.ceil(highestPrice / 100) * 100); // 向上取整到百位
          setPriceRange(highestPrice); // 初始化价格范围为最高价格
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data from Firestore:", error);
        setError("无法加载数据，请稍后再试");
        setLoading(false);
      }
    };

    fetchCategoriesAndProducts();
  }, []);

  // 处理筛选和排序
  useEffect(() => {
    if (!products.length) return;
    
    let result = [...products];

    // 分类筛选
    if (selectedCategory) {
      result = result.filter(product => 
        product.categoryId === selectedCategory
      );
    }

    // 搜索筛选
    if (searchTerm) {
      result = result.filter(product => 
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (product.tags && product.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
      );
    }

    // 价格筛选
    if (priceRange > 0) {
      result = result.filter(product => product.price <= priceRange);
    }

    // 排序
    if (sortOrder === 'newest') {
      // 按创建时间排序
      result = [...result].sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return b.createdAt.seconds - a.createdAt.seconds;
        }
        return 0;
      });
    } else if (sortOrder === 'oldest') {
      // 按创建时间排序
      result = [...result].sort((a, b) => {
        if (a.createdAt && b.createdAt) {
          return a.createdAt.seconds - b.createdAt.seconds;
        }
        return 0;
      });
    }

    if (sortByPrice === 'high') {
      result = [...result].sort((a, b) => b.price - a.price);
    } else if (sortByPrice === 'low') {
      result = [...result].sort((a, b) => a.price - b.price);
    }

    setFilteredProducts(result);
  }, [products, searchTerm, priceRange, sortOrder, sortByPrice, selectedCategory]);

  // 处理分类变更
  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
  };

  // 处理价格滑块变更
  const handlePriceChange = (event, newValue) => {
    setPriceRange(newValue);
  };

  // 处理搜索
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // 处理排序变更
  const handleSortOrderChange = (event) => {
    setSortOrder(event.target.value);
  };

  const handlePriceSortChange = (event) => {
    setSortByPrice(event.target.value);
  };

  return (
    <Box>
      {/* SEO优化 */}
      <SeoHead
        title="能量水晶手串 | 提升运势、平衡能量 | DestinyCrystal"
        description="精选高品质能量水晶手串，包括紫水晶、粉晶、黑曜石等，帮助平衡能量、增强运势。每款水晶都经过专业净化与加持，立即选购适合您的水晶手串！"
        canonicalUrl="https://destiny-crystal.com/shop"
        imageUrl="https://destiny-crystal.com/assets/images/shop/shopBg.png"
        keywords={["水晶手串", "能量水晶", "紫水晶", "粉晶", "黑曜石", "平衡能量", "提升运势"]}
        schemaData={shopSchemaData}
      />
      {/* 英雄区域 */}
      <HeroSection>
        <HeroImage src={heroImage} alt="Shop Background" />
        <HeroContent>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Know Yourself & Those Around You
          </Typography>
          <Box display="flex" justifyContent="center" mt={2}>
            <Button 
              variant="outlined" 
              color="inherit"
              component="a"
              href="/fortune-teller"
              sx={{ 
                borderColor: 'white',
                '&:hover': { borderColor: 'white', background: 'rgba(255,255,255,0.1)' },
                px: 3,
                py: 1
              }}
            >
              Fortune Telling
            </Button>
          </Box>
        </HeroContent>
      </HeroSection>

      {/* 商店主体 */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4}>
          {/* 侧边筛选 */}
          <Grid item xs={12} md={3}>
            <FilterSection>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                产品分类
              </Typography>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Product Classification
              </Typography>

              <CategoryRadioGroup
                value={selectedCategory}
                onChange={handleCategoryChange}
              >
                <FormControlLabel
                  key="all"
                  value=""
                  control={<Radio color="primary" />}
                  label="全部分类 (All)"
                  sx={{ '& .MuiFormControlLabel-label': { fontWeight: 'medium' } }}
                />
                {categories.map(category => (
                  <FormControlLabel
                    key={category.id}
                    value={category.id}
                    control={<Radio color="primary" />}
                    label={`${category.icon || ''} ${category.name}`}
                    sx={{ '& .MuiFormControlLabel-label': { fontWeight: 'medium' } }}
                  />
                ))}
              </CategoryRadioGroup>

              <Box mt={4}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  价格分类
                </Typography>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Price Classification
                </Typography>
                <Box mt={2}>
                  <Typography variant="caption">价格 price</Typography>
                  <Slider
                    value={priceRange}
                    onChange={handlePriceChange}
                    min={0}
                    max={maxPrice}
                    step={10}
                    valueLabelDisplay="auto"
                    sx={{ 
                      mt: 2, 
                      color: '#9F7AEA',
                      '& .MuiSlider-thumb': {
                        width: 16,
                        height: 16,
                        backgroundColor: '#9F7AEA'
                      },
                      '& .MuiSlider-track': {
                        backgroundColor: '#9F7AEA'
                      }
                    }}
                  />
                  <Box display="flex" justifyContent="space-between" mt={1}>
                    <Typography variant="body2">S${priceRange}</Typography>
                    <Typography variant="body2">S${maxPrice}</Typography>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  sx={{ 
                    mt: 2, 
                    width: '100%', 
                    backgroundColor: '#9F7AEA',
                    '&:hover': { backgroundColor: '#7d5ba6' },
                    mb: 2
                  }}
                >
                  Filter
                </Button>
              </Box>
            </FilterSection>
          </Grid>

          {/* 产品网格 */}
          <Grid item xs={12} md={9}>
            {/* 排序和搜索 */}
            <Box 
              display="flex" 
              flexDirection={{ xs: 'column', sm: 'row' }} 
              justifyContent="flex-end" 
              alignItems="center" 
              mb={4}
              gap={2}
            >
              <FormControl 
                variant="outlined" 
                size="small" 
                sx={{ minWidth: 200 }}
              >
                <InputLabel>上架时间 Shelf time</InputLabel>
                <Select
                  value={sortOrder}
                  onChange={handleSortOrderChange}
                  label="上架时间 Shelf time"
                >
                  <MenuItem value="">
                    <em>默认排序</em>
                  </MenuItem>
                  <MenuItem value="newest">最新上架</MenuItem>
                  <MenuItem value="oldest">最早上架</MenuItem>
                </Select>
              </FormControl>

              <FormControl 
                variant="outlined" 
                size="small" 
                sx={{ minWidth: 200 }}
              >
                <InputLabel>价格 price</InputLabel>
                <Select
                  value={sortByPrice}
                  onChange={handlePriceSortChange}
                  label="价格 price"
                >
                  <MenuItem value="">
                    <em>默认排序</em>
                  </MenuItem>
                  <MenuItem value="high">价格高到低</MenuItem>
                  <MenuItem value="low">价格低到高</MenuItem>
                </Select>
              </FormControl>

              <TextField
                variant="outlined"
                size="small"
                placeholder="搜索产品"
                value={searchTerm}
                onChange={handleSearchChange}
                sx={{ 
                  minWidth: 200,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    backgroundColor: 'white',
                    boxShadow: 'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px'
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* 加载指示器 */}
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="300px">
                <CircularProgress sx={{ color: '#9F7AEA' }} />
              </Box>
            ) : (
              <>
                {/* 产品列表 */}
                <Grid container spacing={3}>
                  {filteredProducts.map((product) => (
                    <Grid item xs={12} sm={6} md={4} key={product.id}>
                      <ProductCard 
                        product={{
                          id: product.id,
                          name: product.name,
                          price: product.price,
                          image: product.images && product.images.length > 0 ? product.images[0] : 'https://via.placeholder.com/500x500?text=No+Image',
                          description: product.description || '暂无描述',
                          discountPrice: product.discountPrice
                        }} 
                      />
                    </Grid>
                  ))}
                </Grid>

                {/* 空结果提示 */}
                {!loading && filteredProducts.length === 0 && (
                  <Box textAlign="center" py={8}>
                    <Typography variant="h6" color="text.secondary">
                      没有找到匹配的产品
                    </Typography>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        setSearchTerm('');
                        setPriceRange(maxPrice);
                        setSelectedCategory('');
                        setSortOrder('');
                        setSortByPrice('');
                      }}
                      sx={{ mt: 2 }}
                    >
                      清除筛选条件
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Shop;